import { Fragment } from 'preact';
import { Link } from 'wouter-preact';
import { ArrowRight } from 'lucide-preact';
import type { ClassFilter, Group, Analysis } from '@teacher-shared/analysis';
import { me } from '@/lib/teaching/teacher-api';
import { SetupChecklist } from '@/components/teaching/SetupChecklist';
import { TeachingPage, ClassSwitcher, useClass, WithData, useAnalysis, AssessmentSwitcher, ProvisionalBanner, breadcrumbOf, teachHref, studentHref, Card, MasteryDots, DotsLegend, CodeTag, Plain, plainTitle, Initials, perfFill, INK } from '@/components/teaching/ui';

function greeting(): { hello: string; when: string } {
  const first = (me.value?.name ?? '').trim().split(/\s+/)[0] || me.value?.display_title || '';
  const h = new Date().getHours();
  const part = h < 12 ? 'morning' : h < 17 ? 'afternoon' : 'evening';
  const day = new Date().toLocaleDateString('en-AU', { weekday: 'long', day: 'numeric', month: 'long' });
  return { hello: `Assalamu alaikum${first ? `, ${first}` : ''}`, when: `Good ${part}. ${day}.` };
}

function Ring({ rate }: { rate: number }) {
  const c = 2 * Math.PI * 15;
  return (
    <svg viewBox="0 0 36 36" class="w-14 h-14 shrink-0">
      <circle cx="18" cy="18" r="15" fill="none" stroke="var(--color-border)" stroke-width="4" />
      <circle cx="18" cy="18" r="15" fill="none" stroke="var(--color-accent)" stroke-width="4" stroke-linecap="round"
        stroke-dasharray={`${c * rate} ${c}`} transform="rotate(-90 18 18)" />
    </svg>
  );
}

function Kpi({ value, label, tone, side }: { value: string | number; label: string; tone?: string; side?: preact.ComponentChildren }) {
  return (
    <Card class="!flex-row items-center gap-3 !p-4">
      {side && <div class="order-first">{side}</div>}
      <div>
        <div class="text-[28px] md:text-[30px] font-semibold tracking-[-0.03em] leading-none tabular-nums" style={{ color: tone }}>{value}</div>
        <div class="text-[12px] text-[var(--color-text-muted)] mt-1">{label}</div>
      </div>
    </Card>
  );
}

function MiniBars({ values, highlight }: { values: number[]; highlight?: number }) {
  const max = Math.max(1, ...values);
  return (
    <div class="ml-auto flex items-end gap-[3px] h-9">
      {values.map((v, i) => (
        <i key={i} class="block w-[7px] rounded-[2px]" style={{ height: `${Math.max(8, (v / max) * 100)}%`, background: i === highlight ? '#ef4444' : 'var(--color-accent)', opacity: i === highlight ? 0.9 : 0.6 }} />
      ))}
    </div>
  );
}

const PLAN_STYLE: Record<Group['kind'], { label: string; border: string }> = {
  reteach: { label: 'Reteach', border: '#ef4444' },
  small: { label: 'Small group', border: 'var(--color-border)' },
  individual: { label: 'One-to-one', border: 'var(--color-border)' },
  extension: { label: 'Extend', border: '#22c55e' },
};

export function Home() {
  return <WithData state={useAnalysis()} title="Teaching" empty={<FirstUse />}>{({ a, A }) => <HomeView a={a} A={A} />}</WithData>;
}

/** No test yet: the greeting and the checklist are the whole page. */
function FirstUse() {
  const hi = greeting();
  return (
    <TeachingPage title={hi.hello} breadcrumb={hi.when}>
      <SetupChecklist />
      <Card title="Start with your first test">
        <p class="text-[13px] text-[var(--color-text-muted)] mb-3">Add your classes and students, then set up a test and its questions. This page fills with who needs help, common mistakes and a plan for the week as marks arrive.</p>
        <div class="flex flex-wrap gap-2">
          <Link href="/setup/classes" class="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[13px]">Classes and students</Link>
          <Link href="/setup/tests/new" class="px-3 py-1.5 rounded-lg bg-[var(--color-accent)] text-[13px]" style={{ color: '#ffffff' }}>New test</Link>
          <Link href="/week/commitments" class="px-3 py-1.5 rounded-lg border border-[var(--color-border)] text-[13px]">Your commitments</Link>
        </div>
      </Card>
    </TeachingPage>
  );
}

function HomeView({ a, A }: { a: string; A: Analysis }) {
  const [cls, setCls] = useClass(A.classes);
  const all = A.summariesIn(cls);
  const done = all.filter((s) => s.student.status === 'completed');
  const need = A.struggling(cls);
  const groups = A.groupsIn(cls);
  const ext = groups.filter((g) => g.kind === 'extension').flatMap((g) => g.students);
  const awaiting = all.filter((s) => s.student.status !== 'completed');

  let m = 0, n = 0;
  for (const it of A.items) { const r = A.itemRate(it, cls); m += r.mastered; n += r.n; }
  const avg = n ? m / n : 0;

  const perClass = (f: (c: ClassFilter) => number) => A.classes.map((c) => f(c));
  const hiIdx = (vals: number[]) => vals.indexOf(Math.max(...vals));

  const topics = Array.from(new Set(A.items.map((i) => i.topic)));
  const grid = A.classes.map((c) => Object.fromEntries(A.bucketRates('topic', c).map((b) => [b.label, b.r])));
  const mistakes = A.misconceptionRows(cls).slice(0, 5);
  const mmax = Math.max(1, ...mistakes.map((x) => x.students.length));

  // Plan: every reteach, the biggest small groups, extension last.
  const plan = [
    ...groups.filter((g) => g.kind === 'reteach'),
    // Skip a small group that just repeats a whole-class reteach topic.
    ...groups.filter((g) => g.kind === 'small' && !groups.some((r) => r.kind === 'reteach' && r.cls === g.cls && !!g.topic && r.title.toLowerCase().includes(g.topic.toLowerCase())))
      .sort((a, b) => b.students.length - a.students.length),
  ].slice(0, 3);
  const extReason = Array.from(new Set(groups.filter((g) => g.kind === 'extension').map((g) => g.reason))).join('; ');
  const extGroup: Group | undefined = ext.length
    ? { kind: 'extension', cls: 'all', title: `${ext.length} ready`, reason: extReason, students: ext }
    : undefined;

  const hi = greeting();
  const labels = A.items.map((it) => `${it.id} ${it.prompt}`);
  return (
    <TeachingPage title={hi.hello} breadcrumb={`${hi.when} ${breadcrumbOf(A)}`} banner={<ProvisionalBanner A={A} />} actions={<><AssessmentSwitcher current={a} /><ClassSwitcher value={cls} onChange={setCls} classes={A.classes} /></>} fill>
      {/* W14: stays above the dashboard until done or hidden. Renders nothing otherwise. */}
      <SetupChecklist />
      <div class="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Kpi value={`${Math.round(avg * 100)}%`} label="Average mastery" side={<Ring rate={avg} />} />
        <Card class="!flex-row items-center !p-4">
          <div>
            <div class="text-[28px] md:text-[30px] font-semibold tracking-[-0.03em] leading-none tabular-nums text-[#ef4444]">{need.length}<span class="ml-1.5 text-[13px] font-normal tracking-normal text-[var(--color-text-faint)]">of {done.length}</span></div>
            <div class="text-[12px] text-[var(--color-text-muted)] mt-1">Need support</div>
          </div>
          {cls === 'all' && <MiniBars values={perClass((c) => A.struggling(c).length)} highlight={hiIdx(perClass((c) => A.struggling(c).length))} />}
        </Card>
        <Card class="!flex-row items-center !p-4">
          <div>
            <div class="text-[28px] md:text-[30px] font-semibold tracking-[-0.03em] leading-none tabular-nums">{ext.length}<span class="ml-1.5 text-[13px] font-normal tracking-normal text-[var(--color-text-faint)]">of {done.length}</span></div>
            <div class="text-[12px] text-[var(--color-text-muted)] mt-1">Ready to extend</div>
          </div>
          {cls === 'all' && <MiniBars values={perClass((c) => A.groupsIn(c).filter((g) => g.kind === 'extension').flatMap((g) => g.students).length)} />}
        </Card>
        <Card class="!flex-row items-center !p-4">
          <div>
            <div class="text-[28px] md:text-[30px] font-semibold tracking-[-0.03em] leading-none tabular-nums">{awaiting.length}</div>
            <div class="text-[12px] text-[var(--color-text-muted)] mt-1">Awaiting evidence</div>
          </div>
          <div class="ml-auto text-right text-[11.5px] text-[var(--color-text-muted)] leading-snug">
            {awaiting.filter((s) => s.student.status === 'absent').length} absent<br />
            {awaiting.filter((s) => s.student.status === 'catchup').length} catch-up
          </div>
        </Card>
      </div>

      <div class="grid lg:grid-cols-12 gap-3 lg:flex-1 lg:min-h-0 lg:grid-rows-[minmax(0,1fr)_auto]">
        <Card title="Who needs help first" right={<span class="text-[11px] font-semibold px-2 py-0.5 rounded-full bg-[var(--color-accent-soft)] text-[var(--color-accent)]">{need.length}</span>} class="lg:col-span-5 lg:row-span-2">
          <div class="flex-1 min-h-0 overflow-y-auto -mx-1">
            {need.slice(0, 12).map((s) => (
              <Link key={s.student.id} href={studentHref(a, s.student.id, cls)}
                class="flex items-center gap-3 px-1 py-2 rounded-lg hover:bg-[var(--color-elevated)] border-b border-[var(--color-border)] last:border-0">
                <Initials name={s.student.name} />
                <div class="min-w-0 flex-1">
                  <div class="text-[13px] font-semibold text-[var(--color-text)]">{s.student.name} <span class="font-normal text-[11px] text-[var(--color-text-faint)]">{s.student.cls}</span></div>
                  <div class="text-[11.5px] text-[var(--color-text-muted)] line-clamp-2 leading-snug" title={s.reasons[0] ? plainTitle(s.reasons[0]) : undefined}>{s.reasons[0] ? <Plain text={s.reasons[0]} /> : null}</div>
                </div>
                <div class="hidden sm:block"><MasteryDots labels={labels} marks={A.items.map((it) => { const r = s.results.find((x) => x.itemId === it.id); return r?.notMarked ? null : A.mastered(r); })} /></div>
              </Link>
            ))}
          </div>
          {need.length > 0 && <div class="hidden sm:block mt-2"><DotsLegend first={A.items[0]?.id ?? ''} last={A.items[A.items.length - 1]?.id ?? ''} /></div>}
          <Link href={teachHref('/teaching/students', { a, class: cls })} class="mt-2 inline-flex items-center gap-1 text-[12px] font-medium text-[var(--color-accent)]">
            See all {need.length} with reasons <ArrowRight size={13} />
          </Link>
        </Card>

        <Card title="Topic mastery by class" sub={A.topicRuleApplies ? 'Mastery % on marked answers' : 'Mastery % on marked answers. No topic has 3 or more questions, so no topic is flagged as weak.'} class="lg:col-span-4">
          <div class="grid gap-1 text-[11.5px]" style={{ gridTemplateColumns: `minmax(96px, 1.4fr) repeat(${A.classes.length}, 1fr)` }}>
            <div />
            {A.classes.map((c) => <div key={c} class="text-center text-[var(--color-text-muted)] font-medium">{c}</div>)}
            {topics.map((t) => (
              <Fragment key={t}>
                <div class="text-[var(--color-text-muted)] py-1 leading-tight self-center" title={t}>{t}</div>
                {grid.map((g, i) => {
                  const r = g[t];
                  const ok = r && r.n >= 10;
                  return (
                    <Link key={t + i} href={teachHref('/teaching/heatmap', { a, class: A.classes[i] })}
                      title={ok ? `${t}, ${A.classes[i]}: ${Math.round((r.rate ?? 0) * 100)}% mastery` : `${t}, ${A.classes[i]}: not enough marked answers yet`}
                      class="rounded-md text-center py-1 font-semibold tabular-nums hover:ring-2 hover:ring-[var(--color-accent)]"
                      style={{ background: ok ? perfFill(r.rate) : 'var(--color-elevated)', color: ok ? INK : 'var(--color-text-faint)' }}>
                      {ok ? Math.round((r.rate ?? 0) * 100) : '-'}
                    </Link>
                  );
                })}
              </Fragment>
            ))}
          </div>
        </Card>

        <Card title="Top mistakes" class="lg:col-span-3">
          <div class="flex flex-col gap-2.5 lg:overflow-y-auto lg:min-h-0">
            {mistakes.length === 0 && <div class="text-[12.5px] text-[var(--color-text-muted)]">No mistakes recorded yet. They appear here as you mark.</div>}
            {mistakes.map((x) => (
              <Link key={x.code} href={teachHref('/teaching/misconceptions', { a, class: cls })} class="block group">
                <div class="flex justify-between text-[12px]">
                  <span class="min-w-0 text-[var(--color-text)] leading-snug" title={`${x.meaning} (${x.code})`}>{x.meaning}<CodeTag code={x.code} /></span>
                  <span class="tabular-nums font-semibold text-[var(--color-text)] ml-2">{x.students.length}</span>
                </div>
                <div class="h-[7px] mt-1 rounded-full bg-[var(--color-elevated)] overflow-hidden">
                  <i class="block h-full rounded-full bg-[#ef4444]/80 group-hover:bg-[#ef4444]" style={{ width: `${(x.students.length / mmax) * 100}%` }} />
                </div>
              </Link>
            ))}
          </div>
        </Card>

        <Card title="This week's plan" right={<Link href={teachHref('/teaching/groups', { a, class: cls })} class="text-[11.5px] font-medium text-[var(--color-accent)]">All groups</Link>} class="lg:col-span-7">
          <div class="grid grid-cols-2 md:grid-cols-4 gap-2.5">
            {[...plan, ...(extGroup ? [extGroup] : [])].map((g) => (
              <Link key={g.kind + g.title + g.cls} href={teachHref('/teaching/groups', { a, class: g.kind === 'extension' ? cls : g.cls })}
                class="rounded-xl border p-3 flex flex-col gap-1 hover:shadow-md transition-shadow" style={{ borderColor: PLAN_STYLE[g.kind].border }}>
                <div class="text-[10.5px] font-semibold uppercase tracking-wider text-[var(--color-text-faint)]">{PLAN_STYLE[g.kind].label}{g.kind !== 'extension' ? ` · ${g.cls}` : ''}</div>
                <div class="text-[16px] font-semibold text-[var(--color-text)] leading-tight">
                  {g.kind === 'reteach' ? g.title.replace(/^Reteach (.*) to all of .*$/, '$1').replace(/^./, (c) => c.toUpperCase()) : g.kind === 'small' ? g.title.replace(/, [^,]+$/, '') : g.title}{g.code && <CodeTag code={g.code} />}
                </div>
                <div class="text-[11.5px] text-[var(--color-text-muted)] line-clamp-2">{g.kind === 'reteach' ? g.reason : g.kind === 'small' ? `${g.students.length} students · ${g.reason.toLowerCase()}` : g.reason}</div>
                {g.kind !== 'reteach' && (
                  <div class="flex mt-1">
                    {g.students.slice(0, 4).map((s, i) => (
                      <span key={s.id} class="w-6 h-6 rounded-full grid place-items-center text-[9px] font-bold bg-[var(--color-accent-soft)] text-[var(--color-accent)] border-2 border-[var(--color-card)]" style={{ marginLeft: i ? -6 : 0 }}>
                        {s.name.split(' ').map((p) => p[0]).join('')}
                      </span>
                    ))}
                    {g.students.length > 4 && <span class="w-6 h-6 rounded-full grid place-items-center text-[9px] font-bold bg-[var(--color-elevated)] text-[var(--color-text-muted)] border-2 border-[var(--color-card)] -ml-1.5">+{g.students.length - 4}</span>}
                  </div>
                )}
              </Link>
            ))}
          </div>
        </Card>
      </div>
      {done.length === 0 && <div class="text-[13px] text-[var(--color-text-muted)]">No marked papers yet. Enter marks or upload scans in Marks to fill this page.</div>}
    </TeachingPage>
  );
}
